import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { translate } from 'react-i18next';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from 'antd/lib/button';
import Alert from 'antd/lib/alert';

import authApi, { apiCallIds } from '../../../auth/api';
import {
  fetchMeActions,
  fetchPendingContractsActions,
  fetchUnsignedContractsActions,
  removeFromPendingContracts,
  removeFromUnsignedContracts,
  selectAccountId,
  selectToken,
} from '../../../auth/ducks';
import { fetchAccountContractsActions, selectContracts } from '../../../documents/ducks';
import { selectIsInProgress } from '../../../spinner/ducks';
import { ConfirmContractModal } from '../../../../common/modals';
import { contractStatus, contractType } from '../../../../common/enums';
import { getFileUrl } from '../../../../common/utils/helpers';

const mapStateToProps = state => ({
  accountId: selectAccountId(state),
  token: selectToken(state),
  contracts: selectContracts(state),
  isSigning: selectIsInProgress(state, apiCallIds.SIGN_INFO_OBLIGATIONS_INVESTOR),
  isConfirming: selectIsInProgress(state, apiCallIds.CONFIRM_INFO_OBLIGATIONS_INVESTOR),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(
    {
      fetchAccountContracts: fetchAccountContractsActions.request,
      fetchMe: fetchMeActions.request,
      fetchPendingContracts: fetchPendingContractsActions.request,
      fetchUnsignedContracts: fetchUnsignedContractsActions.request,
      removeFromPendingContracts,
      removeFromUnsignedContracts,
    },
    dispatch
  ),
});

@connect(mapStateToProps, mapDispatchToProps)
@translate()
export default class SignInvestorInfoObligationsBlock extends Component {
  static propTypes = {
    accountId: PropTypes.string.isRequired,
    token: PropTypes.string.isRequired,
    contracts: PropTypes.array.isRequired,
    isSigning: PropTypes.bool.isRequired,
    isConfirming: PropTypes.bool.isRequired,
    actions: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired,
  };

  state = {
    confirmModalVisible: false,
    error: null,
  };

  componentDidMount() {
    this.refreshContracts();
  }

  refreshContracts = () => {
    const { actions, accountId } = this.props;
    actions.fetchAccountContracts(accountId);
    actions.fetchUnsignedContracts({ accountId });
    actions.fetchPendingContracts({ accountId });
  };

  getInfoContract() {
    const { contracts } = this.props;
    return contracts.find(c => c.type === contractType.INFORMATION_OBLIGATIONS_INVESTOR) || null;
  }

  getDocumentUrl() {
    const { token } = this.props;
    const contract = this.getInfoContract();
    return contract && contract.fileId ? getFileUrl(contract.fileId, token) : null;
  }

  handleSign = async () => {
    const { accountId, t } = this.props;
    const response = await authApi.signInfoObligationsInvestor(accountId);
    if (response.ok) {
      this.setState({ confirmModalVisible: true, error: null });
      this.refreshContracts();
    } else {
      this.setState({ error: t('Podpis sa nepodaril. Skúste znova.') });
    }
  };

  handleConfirm = async token => {
    const { actions, accountId, t } = this.props;
    const infoContract = this.getInfoContract();
    const response = await authApi.confirmInfoObligationsInvestor(accountId, token);
    if (response.ok) {
      this.setState({ confirmModalVisible: false, error: null });
      if (infoContract) {
        actions.removeFromUnsignedContracts(infoContract.id);
        actions.removeFromPendingContracts(infoContract.id);
      }
      this.refreshContracts();
      actions.fetchMe({ accountId });
    } else {
      this.setState({ error: t('SMS kód sa nepodarilo potvrdiť. Skúste znova.') });
    }
  };

  handleResendSmsCode = async callbacks => {
    const { accountId, t } = this.props;
    try {
      const response = await authApi.resendInfoObligationsInvestorSmsCode(accountId);
      if (response.ok) {
        this.setState({ error: null });
        if (callbacks && callbacks.onSuccess) {
          callbacks.onSuccess();
        }
      } else {
        this.setState({ error: t('SMS kód sa nepodarilo preposlať. Skúste znova neskôr.') });
        if (callbacks && callbacks.onError) {
          callbacks.onError(response.error);
        }
      }
    } catch (error) {
      this.setState({ error: t('SMS kód sa nepodarilo preposlať. Skúste znova neskôr.') });
      if (callbacks && callbacks.onError) {
        callbacks.onError(error);
      }
    }
  };

  hideConfirmModal = () => {
    this.setState({ confirmModalVisible: false });
  };

  renderDocumentLink() {
    const { t } = this.props;
    const documentUrl = this.getDocumentUrl();
    if (!documentUrl) {
      return null;
    }

    return (
      <a className="info-obligations-read-link" href={documentUrl} target="_blank" rel="noopener noreferrer">
        {t('Prečítať dokument Informačné povinnosti poskytovateľa služieb')}
      </a>
    );
  }

  render() {
    const { accountId, isSigning, isConfirming, t } = this.props;
    const { confirmModalVisible, error } = this.state;
    const infoContract = this.getInfoContract();

    if (!infoContract || infoContract.status === contractStatus.CONFIRMED) {
      return null;
    }

    const isPending = infoContract.status === contractStatus.PENDING_CONFIRMATION;

    return (
      <div className="investor-info-obligations">
        <Alert
          type="warning"
          showIcon
          message={t('Pred pokračovaním je potrebné podpísať Informačné povinnosti poskytovateľa služieb.')}
          description={t('Dokument sa podpisuje samostatne cez SMS kód.')}
        />
        {error && (
          <p style={{ color: 'red' }}>{error}</p>
        )}
        <div className="ant-modal-footer info-obligations-actions">
          {this.renderDocumentLink()}
          <Button
            size="small"
            type="primary"
            loading={isSigning}
            onClick={isPending ? () => this.setState({ confirmModalVisible: true }) : this.handleSign}
          >
            {isPending ? t('Zadať SMS kód') : t('Podpísať Informačné povinnosti')}
          </Button>
        </div>
        {confirmModalVisible && (
          <ConfirmContractModal
            hideModal={this.hideConfirmModal}
            confirmBy="investorInfoObligations"
            isLoading={isConfirming}
            onResendSmsCode={this.handleResendSmsCode}
            resendCooldownKey={`investor-info-obligations:${accountId}`}
            errorMessage={error}
            onSubmit={this.handleConfirm}
          />
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/features/dashboard/components/registration-action-alert/SignInvestorInfoObligationsBlock.js